/**
 * =====================================================
 * GLOBAL DISCOUNT CONFIGURATION
 * =====================================================
 *
 * ┌───────────────────────────────────────────────────┐
 * │  TO CHANGE THE SALE, EDIT THE TWO VALUES BELOW.   │
 * │  Nothing else in the project needs to change.     │
 * └───────────────────────────────────────────────────┘
 *
 * Everything updates automatically: the announcement strip,
 * the big sale banner on the home page, all product prices,
 * the cart and the checkout.
 *
 * =====================================================
 */

/**
 * ❶  THE DISCOUNT
 * ===============
 * Write the percentage you want, as a whole number.
 *
 *    50   →  50% off
 *    30   →  30% off
 *     0   →  sale OFF (banner disappears, prices go back to normal)
 *
 * Any whole number from 0 to 90 works.
 */
export const DISCOUNT_PERCENT = 50;

/**
 * ❷  THE END DATE
 * ===============
 * Shown on the banner. Write it however you want it to read.
 */
export const SALE_END_DATE = '31 August';

/* =====================================================
 * Below this line is the machinery. You don't need to
 * edit it — but nothing breaks if you read it.
 * ===================================================== */

// Safety limits. A discount over 90% is almost always a typo
// (e.g. writing 500 instead of 50), so we refuse it rather than
// selling everything for nothing.
const MIN_PERCENT = 0;
const MAX_PERCENT = 90;

const isValidPercent = (value) =>
  typeof value === 'number' &&
  Number.isFinite(value) &&
  value >= MIN_PERCENT &&
  value <= MAX_PERCENT;

if (!isValidPercent(DISCOUNT_PERCENT)) {
  console.error(
    `Invalid DISCOUNT_PERCENT value: ${DISCOUNT_PERCENT}. ` +
    `Please use a number between ${MIN_PERCENT} and ${MAX_PERCENT} ` +
    `(for example: 50 for 50% off). Falling back to 0 (no discount).`
  );
}

/**
 * The validated percentage as a whole number, e.g. 50
 */
export const getDiscountPercent = () =>
  isValidPercent(DISCOUNT_PERCENT) ? DISCOUNT_PERCENT : 0;

/**
 * The discount as a decimal for maths, e.g. 0.5
 * Kept for compatibility with anything already importing GLOBAL_DISCOUNT.
 */
export const GLOBAL_DISCOUNT = getDiscountPercent() / 100;

export const getGlobalDiscount = () => getDiscountPercent() / 100;

/**
 * Is a sale running right now?
 */
export const isDiscountActive = () => getDiscountPercent() > 0;

/**
 * The percentage as a display string, e.g. "50%"
 * Returns an empty string when no sale is running.
 */
export const getDiscountPercentage = () => {
  const percent = getDiscountPercent();
  return percent > 0 ? `${percent}%` : '';
};

/**
 * The sale end date, for display.
 */
export const getSaleEndDate = () => SALE_END_DATE;

/**
 * Round to whole cents, so what the customer sees and what
 * they are charged can never drift apart.
 */
const toCents = (amount) => Math.round(amount * 100) / 100;

/**
 * Calculate the discounted price.
 * @param {number} originalPrice
 * @returns {number} the price after discount
 */
export const calculateDiscountedPrice = (originalPrice) => {
  const discount = getGlobalDiscount();
  if (discount === 0 || !originalPrice) {
    return originalPrice;
  }
  return toCents(originalPrice * (1 - discount));
};

/**
 * Everything a price display needs, in one object.
 * @param {number} originalPrice
 * @returns {object} { originalPrice, discountedPrice, hasDiscount, discountPercentage, savings }
 */
export const getPriceInfo = (originalPrice) => {
  const discount = getGlobalDiscount();
  const hasDiscount = discount > 0 && originalPrice > 0;
  const discountedPrice = hasDiscount
    ? toCents(originalPrice * (1 - discount))
    : originalPrice;

  return {
    originalPrice,
    discountedPrice,
    hasDiscount,
    discountPercentage: hasDiscount ? `${getDiscountPercent()}%` : '',
    // Derived from the rounded price so savings always add up exactly.
    savings: hasDiscount ? toCents(originalPrice - discountedPrice) : 0
  };
};

export default {
  DISCOUNT_PERCENT,
  SALE_END_DATE,
  GLOBAL_DISCOUNT,
  getDiscountPercent,
  getGlobalDiscount,
  isDiscountActive,
  getDiscountPercentage,
  getSaleEndDate,
  calculateDiscountedPrice,
  getPriceInfo
};
